import { useModalStatus } from "@privy-io/react-auth"
import { wrap } from "@reatom/core"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { z } from "zod"
import {
  accountSettingsRequest,
  promoStatusSchema,
} from "../settings/account-settings-api"
import { useAccountEmailAuth } from "../settings/use-account-email-auth"
import { useEmailBinding } from "../settings/use-email-binding"
import { notificationPreferenceQueryKey } from "../settings/use-notification-preferences"

export function useLinkedEmailPromptController() {
  const { userId, authenticated, loading, email, bffBaseUrl, getAccessToken } =
    useAccountEmailAuth()
  const { isOpen: isPrivyModalOpen } = useModalStatus()
  const binding = useEmailBinding()
  const client = useQueryClient()
  const [dismissed, setDismissed] = useState(false)
  const [started, setStarted] = useState(false)
  const autoDismissStarted = useRef(false)
  const saving = useRef(false)
  const currentAccount = useRef(userId)
  currentAccount.current = userId
  useEffect(
    () => () => {
      currentAccount.current = null
    },
    [],
  )
  const options = { bffBaseUrl, accountId: userId ?? "", getAccessToken }
  const eligibility = useQuery({
    queryKey: ["email-promo", userId],
    queryFn: async () =>
      promoStatusSchema.parse(
        await wrap(accountSettingsRequest(options, "email-promo")),
      ),
    enabled: authenticated && Boolean(userId) && !loading,
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
  const dismissal = useMutation({
    mutationFn: () => accountSettingsRequest(options, "email-promo", "POST"),
    onSuccess: () => {
      if (currentAccount.current === userId) setDismissed(true)
    },
    onSettled: () => {
      saving.current = false
    },
  })
  const alerts = useMutation({
    mutationFn: async (actionId: string) => {
      z.object({ completed: z.literal(true) }).parse(
        await wrap(
          accountSettingsRequest(options, "email-promo", "PUT", {
            consent: true,
            actionId,
          }),
        ),
      )
    },
    onSuccess: () => {
      if (currentAccount.current !== userId) return
      setDismissed(true)
      void client.invalidateQueries({
        queryKey: notificationPreferenceQueryKey(userId),
      })
    },
    onSettled: () => {
      saving.current = false
    },
  })
  const isAlerts = eligibility.data?.variant === "turn-on-alerts" && !started
  function dismiss() {
    if (binding.pending || saving.current) return
    saving.current = true
    dismissal.mutate()
  }
  useEffect(() => {
    if (started && binding.completed && !autoDismissStarted.current) {
      autoDismissStarted.current = true
      dismiss()
    }
  })
  const pending = binding.pending || alerts.isPending || dismissal.isPending
  const isOpen = Boolean(
    eligibility.data?.eligible &&
      !eligibility.isFetching &&
      !loading &&
      !dismissed &&
      !isPrivyModalOpen &&
      (!email || started || isAlerts) &&
      !(started && binding.pending),
  )
  function submit() {
    if (saving.current || binding.pending) return
    if (isAlerts) {
      const actionId = alerts.variables ?? eligibility.data?.actionId
      if (!actionId) return
      saving.current = true
      alerts.mutate(actionId)
    } else if (binding.completed) {
      dismiss()
    } else if (binding.statusFailed) {
      binding.retryStatus()
    } else if (email) {
      binding.retryConsent()
    } else {
      setStarted(true)
      binding.beginBinding()
    }
  }
  return {
    isOpen,
    pending,
    dismiss,
    submit,
    mode: isAlerts ? "turn-on-alerts" : "bind-email",
    error: dismissal.error?.message ?? alerts.error?.message ?? binding.error,
    actionLabel: binding.completed
      ? "Retry dismissal"
      : binding.statusFailed
        ? "Retry email settings"
        : (email && !isAlerts) || alerts.isError
          ? "Retry consent save"
          : undefined,
  } satisfies {
    isOpen: boolean
    pending: boolean
    dismiss: () => void
    submit: () => void
    mode: "turn-on-alerts" | "bind-email"
    error: string | null
    actionLabel: string | undefined
  }
}
